<template>
  <div class="tasks-layout text-dark">
    <div class="tasks-header">
      <div class="tasks-header__left">
        <DoFiltersFilter
          :title="i18n[locale]['task_names']"
          :tags="taskNameLabels"
          :selectedTags="selectedFilterTaskNames"
          @select="toggleFilterTaskNames"
          @clear="selectedFilterTaskNames = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="i18n[locale]['brick_type']"
          :tags="filterLabels"
          :selectedTags="selectedFilterSubjectTypes"
          @select="toggleFilterSubjectTypes"
          @clear="selectedFilterSubjectTypes = []">
        </DoFiltersFilter>
        <DoFiltersFilter
          :title="i18n[locale]['tasks_status']"
          :tags="taskStatusFilters"
          :selectedTags="selectedFilterTaskStatus"
          @select="toggleFilterTaskStatus"
          @clear="selectedFilterTaskStatus = []">
        </DoFiltersFilter>
      </div>
      <div class="tasks-header__right">
        <div class="do-spinner" v-if="isSubmittingTask"></div>
        <button
          class="do-bttn do-bttn--menu-button"
          content="Download til excel"
          v-tippy="{ arrow: true, placement: 'bottom' }"
          @click="asyncLoadXLSXAndDownload"
          :disabled="isDownloading || !activeGroupData"
        >
          <span v-if="isDownloading" class="do-spinner do-spinner--small"></span>
          <span v-else class="icon-download"></span>
        </button>
        <button class="do-bttn do-bttn--submit" @click="openCreateTask">
          {{ i18n[locale]['create_tasks'] }}
        </button>
      </div>
    </div>
    <div class="tasks-body">
    <div class="tasks-sidebar" :class="{'tasks-sidebar--collapsed': sidebarCollapsed}">
      <div class="sidebar-header">
        <span v-if="!sidebarCollapsed">{{ i18n[locale]['tasks_label'] }} ({{ filteredGroupedTasks.length }} {{ i18n[locale]['of'] }} {{ totalGroupCount }})</span>
        <button class="do-bttn do-bttn--submit sidebar-create-btn" v-if="!sidebarCollapsed" @click="openCreateTask">+</button>
        <button class="sidebar-collapse-btn" @click="sidebarCollapsed = !sidebarCollapsed">
          <span :class="sidebarCollapsed ? 'icon-arrow-right4' : 'icon-arrow-left4'"></span>
        </button>
      </div>
      <template v-if="!sidebarCollapsed">
        <ul class="sidebar-list" v-if="filteredGroupedTasks.length > 0">
          <li class="sidebar-item"
              :class="{'sidebar-item--active': activeGroupName === '__all__'}"
              @click="setActiveGroup('__all__')">
            <span class="sidebar-name">{{ i18n[locale]['all'] }}</span>
            <span class="sidebar-count">{{ Math.round(allFilteredWeightedHours * 10) / 10 }} {{ i18n[locale]['hours_short'] }}</span>
          </li>
          <li v-for="group in filteredGroupedTasks"
              :key="group.name"
              class="sidebar-item"
              :class="{'sidebar-item--active': activeGroupName === group.name}"
              @click="setActiveGroup(group.name)">
            <span class="sidebar-dot" :style="{backgroundColor: group.color}"></span>
            <span class="sidebar-name">{{ group.name }}</span>
            <span class="sidebar-count">{{ Math.round(group.weightedHours * 10) / 10 }} {{ i18n[locale]['hours_short'] }}</span>
          </li>
        </ul>
        <div v-else class="sidebar-empty">{{ i18n[locale]['no_tasks'] }}</div>
      </template>
    </div>
    <div class="tasks-main">
      <template v-if="activeGroupData">
        <div class="task-table-wrap">
          <table class="d-table">
            <thead>
              <tr>
                <th style="width: 220px"></th>
                <th class="d-table__number" style="width: 80px">{{ i18n[locale]['hours'] }}</th>
                <th style="width: 120px">{{ i18n[locale]['type'] }}</th>
                <th style="min-width: 250px"><span class="icon-user3"></span> {{ i18n[locale]['users'] }}</th>
                <th class="d-table__number" style="width: 80px">{{ i18n[locale]['total'] }}</th>
                <th style="width: 50px"></th>
              </tr>
            </thead>
            <tbody>
              <tr v-if="activeGroupName !== '__all__'" class="task-table-row task-table-row--add" @click="duplicateFromGroup">
                <td colspan="6">{{ i18n[locale]['new_task'] }}</td>
              </tr>
              <tr v-for="task in activeGroupData.tasks" :key="task.id">
                <td>
                  <div class="task__name">
                    <div @click="setEditTask(task)"
                         :class="[{'td-subject--hidden': !task.can_schedule}, {'td-subject': task.can_schedule}]"
                         :style="{'background-color': task.color}">
                      {{ task.name || '' }}
                    </div>
                  </div>
                </td>
                <td class="d-table__number">{{ task.hours ? parseFloat(task.hours.toFixed(2)) : 0 }}</td>
                <td class="task-table-type">{{ getTaskTypeLabel(task) }}</td>
                <td @click="setActiveDSelect(task)"
                    :class="{'d-select-lookalike': activeDSelect !== task.id && (task.user_calendar_ids || []).length < 1}">
                  <DSelectCalendarIds v-if="activeDSelect === task.id"
                                      :show="activeDSelectShow"
                                      v-on-clickaway="closeDSelect"
                                      itemType="USER"
                                      :preSelectedItems="task.user_calendar_ids || []"
                                      :showTags="true"
                                      :sortSelectedByName="true"
                                      @update="setTableSelectedUsers($event, task)">
                  </DSelectCalendarIds>
                  <div class="task__users" v-if="activeDSelect !== task.id && (task.user_calendar_ids || []).length > 0">
                    <p v-for="name in getUserNames(task.user_calendar_ids || [])" :key="name">{{ name }}</p>
                  </div>
                </td>
                <td class="d-table__number">{{ getTaskWeightedTotal(task) }}</td>
                <td class="task-table-actions">
                  <button class="task-table-copy" @click.stop="duplicateTask(task)" :title="i18n[locale]['create_copy']">+</button>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
      </template>
      <div v-else class="main-empty">{{ i18n[locale]['select_task'] }}</div>
    </div>
    </div>
    <CreateTask
      v-if="isTaskModal"
      :editTask="editTask"
      :activeFilter="null"
      :prefill="prefillTask"
      :quickCreate="isQuickCreate"
      :isLoadingFreshData="isLoadingFreshTask"
      @close="closeCreateTask"
      @create="createTask"
      @updateTask="updateTask"
    ></CreateTask>
  </div>
</template>

<script>
import CreateTask from './SubjectsDistributionTasksCreate'
import DSelectCalendarIds from '../DSelect/DSelectCalsIds'
import DoFiltersFilter from '../DoFilters/DoFiltersFilter.vue'
import docendo from '../../docendo'
import {mixin as clickaway} from 'vue-clickaway'

let XLSX = null

const I18N = {
  da: {
    create_tasks: 'Opret opgave',
    teaching: 'Undervisning',
    additional_tasks: 'Øvrige opgaver',
    preparation: 'Forberedelse',
    pool: 'Lokalpulje',
    club: 'SFO',
    task_names: 'Opgavens titel',
    brick_type: 'Briktype',
    tasks_label: 'Opgaver',
    of: 'af',
    all: 'Alle',
    hours_short: 't',
    no_tasks: 'Ingen opgaver',
    hours: 'Timer',
    type: 'Type',
    users: 'Brugere',
    total: 'Total',
    new_task: '+ Ny opgave',
    create_copy: 'Opret kopi',
    select_task: 'Vælg en opgave i listen',
    task: 'Opgave',
    tasks_sheet: 'Opgaver',
    tasks_status: 'Status',
    only_tasks_with_users: 'Vis opgaver med personale',
    only_tasks_without_users: 'Vis opgaver uden personale'
  },
  nb: {
    create_tasks: 'Opprett oppgaver',
    teaching: 'Undervisning',
    additional_tasks: 'Øvrige oppgaver',
    preparation: 'Forberedelse',
    pool: 'Planleggingsdager',
    club: 'SFO',
    task_names: 'Oppgave',
    brick_type: 'Brikketype',
    tasks_label: 'Oppgaver',
    of: 'av',
    all: 'Alle',
    hours_short: 't',
    no_tasks: 'Ingen oppgaver',
    hours: 'Timer',
    type: 'Type',
    users: 'Brukere',
    total: 'Totalt',
    new_task: '+ Ny oppgave',
    create_copy: 'Opprett kopi',
    select_task: 'Velg en oppgave i listen',
    task: 'Oppgave',
    tasks_sheet: 'Oppgaver',
    tasks_status: 'Status',
    only_tasks_with_users: 'Vis oppgaver med personale',
    only_tasks_without_users: 'Vis oppgaver uten personale'
  },
  en: {
    create_tasks: 'Create task',
    teaching: 'Teaching',
    additional_tasks: 'Additional tasks',
    preparation: 'Preparation',
    pool: 'Custom pool',
    club: 'Club',
    task_names: 'Task',
    brick_type: 'Task type',
    tasks_label: 'Tasks',
    of: 'of',
    all: 'All',
    hours_short: 'h',
    no_tasks: 'No tasks',
    hours: 'Hours',
    type: 'Type',
    users: 'Users',
    total: 'Total',
    new_task: '+ New task',
    create_copy: 'Create copy',
    select_task: 'Select a task from the list',
    task: 'Task',
    tasks_sheet: 'Tasks',
    tasks_status: 'Status',
    only_tasks_with_users: 'Show tasks with personnel',
    only_tasks_without_users: 'Show tasks without personnel'
  }
}

export default {
  mixins: [clickaway],
  components: { CreateTask, DSelectCalendarIds, DoFiltersFilter },
  data () {
    return {
      locale: window.USER && window.USER.language ? window.USER.language : 'da',
      activeGroup: null,
      isTaskModal: false,
      editTask: null,
      isLoadingFreshTask: false,
      prefillTask: null,
      isQuickCreate: false,
      activeDSelect: null,
      activeDSelectShow: false,
      selectedFilterSubjectTypes: [],
      selectedFilterTaskNames: [],
      selectedFilterTaskStatus: [],
      isDownloading: false,
      sidebarCollapsed: false
    }
  },
  computed: {
    i18n () {
      return I18N
    },
    isSubmittingTask () {
      return this.$store.state.subjects.isSubmittingTask
    },
    isSchoolFeatureLocalePool () {
      return this.$store.getters.isSchoolFeatureLocalePool
    },
    localPoolTitle () {
      const title = this.$store.getters.localPoolTitle
      return title || this.i18n[this.locale]['pool']
    },
    tasks () {
      return this.$store.state.subjects.tasks
    },
    activeSchoolYear () {
      return this.$store.state.localActivatedSchoolYear
    },
    subjectsInHash () {
      return this.$store.getters.subjectsInHash
    },
    allCalendarsInHash () {
      return this.$store.getters.allCalendarsInHash
    },
    visibleTasks () {
      const calendarsHash = this.allCalendarsInHash
      return this.tasks.filter(task => {
        const subjects = task.subjects || []
        if (subjects.length === 0) return true
        return !subjects.every(s => {
          const ownerCal = calendarsHash[s.calendar_id]
          return ownerCal && ownerCal.type === 'klass'
        })
      })
    },
    tasksTeaching () {
      return this.visibleTasks.filter(t => t.subject_type === 'person_resource_visible' || t.subject_type === 'person_resource' || t.subject_type === 'subject')
    },
    tasksTasks () {
      return this.visibleTasks.filter(t => t.subject_type === 'additional_tasks')
    },
    tasksPrep () {
      return this.visibleTasks.filter(t => t.subject_type === 'preparation')
    },
    tasksPool () {
      return this.visibleTasks.filter(t => t.subject_type === 'pool')
    },
    tasksClub () {
      return this.visibleTasks.filter(t => t.subject_type === 'club')
    },
    filters () {
      const filters = [
        {label: this.i18n[this.locale]['teaching'], value: 'teaching', length: this.tasksTeaching.length},
        {label: this.i18n[this.locale]['additional_tasks'], value: 'additional_tasks', length: this.tasksTasks.length},
        {label: this.i18n[this.locale]['preparation'], value: 'preparation', length: this.tasksPrep.length},
        {label: this.localPoolTitle, value: 'pool', length: this.tasksPool.length},
        {label: this.i18n[this.locale]['club'], value: 'club', length: this.tasksClub.length}
      ]
      return this.isSchoolFeatureLocalePool ? filters : filters.filter(f => f.value !== 'pool')
    },
    filterLabels () {
      return this.filters.map(f => f.label)
    },
    selectedFilterSubjectTypesValue () {
      return this.filters
        .filter(f => this.selectedFilterSubjectTypes.includes(f.label))
        .map(f => f.value)
    },
    taskNameLabels () {
      const names = this.visibleTasks
        .map(t => t && t.name ? String(t.name).trim() : '')
        .filter(n => n.length > 0)
      return Array.from(new Set(names)).sort((a, b) => a.localeCompare(b))
    },
    onlyTasksWithUsersI18n () {
      return this.i18n[this.locale].only_tasks_with_users
    },
    onlyTasksWithoutUsersI18n () {
      return this.i18n[this.locale].only_tasks_without_users
    },
    taskStatusFilters () {
      return [this.onlyTasksWithUsersI18n, this.onlyTasksWithoutUsersI18n]
    },
    filteredTasks () {
      const calendarsHash = this.allCalendarsInHash
      const sanitize = task => {
        const all = task.user_calendar_ids || []
        const users = []
        const nonUsers = []
        all.forEach(calId => {
          const cal = calendarsHash[calId]
          if (cal && cal.type === 'user') {
            users.push(calId)
          } else if (calId != null) {
            nonUsers.push(calId)
          }
        })
        users.sort((a, b) => (calendarsHash[a].name || '').localeCompare(calendarsHash[b].name || '', 'da'))
        return Object.assign({}, task, {
          user_calendar_ids: users,
          _nonUserCalendarIds: nonUsers
        })
      }

      const selectedValues = this.selectedFilterSubjectTypesValue
      let base = []
      if (!selectedValues.length || selectedValues.includes('')) {
        base = this.visibleTasks.map(sanitize)
      } else {
        if (selectedValues.includes('teaching')) base = base.concat(this.tasksTeaching.map(sanitize))
        if (selectedValues.includes('additional_tasks')) base = base.concat(this.tasksTasks.map(sanitize))
        if (selectedValues.includes('preparation')) base = base.concat(this.tasksPrep.map(sanitize))
        if (selectedValues.includes('pool')) base = base.concat(this.tasksPool.map(sanitize))
        if (selectedValues.includes('club')) base = base.concat(this.tasksClub.map(sanitize))
      }
      if (this.selectedFilterTaskNames && this.selectedFilterTaskNames.length > 0) {
        const selectedNames = this.selectedFilterTaskNames
        base = base.filter(t => {
          const n = t && t.name ? String(t.name).trim() : ''
          return n.length > 0 && selectedNames.indexOf(n) > -1
        })
      }
      if (this.selectedFilterTaskStatus && this.selectedFilterTaskStatus.length > 0) {
        const hasFilterWithUsers = this.selectedFilterTaskStatus.indexOf(this.onlyTasksWithUsersI18n) > -1
        const hasFilterWithoutUsers = this.selectedFilterTaskStatus.indexOf(this.onlyTasksWithoutUsersI18n) > -1
        base = base.filter(task => {
          const hasUsers = (task.user_calendar_ids || []).length > 0
          return (hasFilterWithUsers && hasUsers) || (hasFilterWithoutUsers && !hasUsers)
        })
      }
      return base
    },
    groupedTasks () {
      const map = {}
      this.filteredTasks.forEach(task => {
        const name = (task.name || '').trim()
        if (!name) return
        if (!map[name]) {
          map[name] = {
            name: name,
            color: task.color || '#ccc',
            tasks: [],
            totalHours: 0,
            weightedHours: 0
          }
        }
        map[name].tasks.push(task)
        const h = parseFloat(task.hours) || 0
        map[name].totalHours += h
        map[name].weightedHours += this.getTaskWeightedTotal(task)
      })
      return Object.values(map).sort((a, b) => a.name.localeCompare(b.name))
    },
    filteredGroupedTasks () {
      return this.groupedTasks
    },
    totalGroupCount () {
      const names = new Set()
      this.visibleTasks.forEach(t => {
        const n = (t.name || '').trim()
        if (n) names.add(n)
      })
      return names.size
    },
    allFilteredWeightedHours () {
      let sum = 0
      this.filteredGroupedTasks.forEach(g => { sum += g.weightedHours })
      return sum
    },
    allGroupData () {
      const allTasks = []
      let totalHours = 0
      let weightedHours = 0
      this.filteredGroupedTasks.forEach(g => {
        g.tasks.forEach(t => allTasks.push(t))
        totalHours += g.totalHours
        weightedHours += g.weightedHours
      })
      const sorted = allTasks.slice().sort((a, b) => {
        const nameCmp = (a.name || '').localeCompare(b.name || '')
        if (nameCmp !== 0) return nameCmp
        return (parseFloat(b.hours) || 0) - (parseFloat(a.hours) || 0)
      })
      return { name: this.i18n[this.locale]['all'], color: '#8898aa', tasks: sorted, totalHours: totalHours, weightedHours: weightedHours }
    },
    activeGroupName () {
      if (this.activeGroup === '__all__') return '__all__'
      if (this.activeGroup) {
        const exists = this.filteredGroupedTasks.find(g => g.name === this.activeGroup)
        if (exists) return this.activeGroup
      }
      return '__all__'
    },
    activeGroupData () {
      if (this.activeGroupName === '__all__') return this.allGroupData
      const group = this.filteredGroupedTasks.find(g => g.name === this.activeGroupName)
      if (!group) return null
      const sorted = group.tasks.slice().sort((a, b) => {
        const nameCmp = (a.name || '').localeCompare(b.name || '')
        if (nameCmp !== 0) return nameCmp
        return (parseFloat(b.hours) || 0) - (parseFloat(a.hours) || 0)
      })
      return Object.assign({}, group, { tasks: sorted })
    }
  },
  watch: {
    activeSchoolYear: {
      immediate: true,
      handler (val) {
        if (!val || !val.id) return
        const schoolId = this.$store.getters.schoolId
        if (!schoolId) return
        docendo.readTasks(schoolId, val.id, null, data => {
          this.$store.commit('setTasks', data)
          window.dispatchEvent(new window.CustomEvent('vueDoneLoadingTasks', {detail: data}))
        }, () => {})
      }
    }
  },
  methods: {
    getTaskWeightedTotal (task) {
      const baseHours = parseFloat(task.hours) || 0
      const userCalIds = task.user_calendar_ids || []
      if (!userCalIds.length) return 0

      const subjectId = task.subjects && task.subjects.length
        ? task.subjects[0].id
        : (task.subject_ids && task.subject_ids.length ? task.subject_ids[0] : null)
      const subj = subjectId ? this.subjectsInHash[subjectId] : null
      const scList = subj && subj.subject_calendars ? subj.subject_calendars : []

      let total = 0
      for (let i = 0; i < userCalIds.length; i++) {
        const sc = scList.find(s => s.calendar_id === userCalIds[i] && s.hours != null)
        total += sc ? parseFloat(sc.hours) : baseHours
      }
      return parseFloat(total.toFixed(2))
    },
    toggleFilter (filter, filterArray) {
      if (filterArray.includes(filter)) {
        filterArray.splice(filterArray.indexOf(filter), 1)
      } else {
        filterArray.push(filter)
      }
    },
    toggleFilterTaskNames (tag) {
      this.toggleFilter(tag, this.selectedFilterTaskNames)
    },
    toggleFilterSubjectTypes (tag) {
      this.toggleFilter(tag, this.selectedFilterSubjectTypes)
    },
    toggleFilterTaskStatus (tag) {
      this.toggleFilter(tag, this.selectedFilterTaskStatus)
    },
    getTaskTypeLabel (task) {
      const t = task.subject_type
      if (t === 'person_resource_visible' || t === 'person_resource' || t === 'subject') return this.i18n[this.locale]['teaching']
      if (t === 'additional_tasks') return this.i18n[this.locale]['additional_tasks']
      if (t === 'preparation') return this.i18n[this.locale]['preparation']
      if (t === 'pool') return this.localPoolTitle
      if (t === 'club') return this.i18n[this.locale]['club']
      return ''
    },
    setActiveGroup (name) {
      this.activeGroup = name
    },
    getUserNames (calIds) {
      return calIds
        .map(calId => this.allCalendarsInHash[calId] ? this.allCalendarsInHash[calId].name : '')
        .filter(n => n.length > 0)
    },
    getCalendarName (calId) {
      const cal = this.allCalendarsInHash[calId]
      return cal ? cal.name : ''
    },
    setEditTask (task) {
      this.isTaskModal = false
      this.isQuickCreate = false
      this.editTask = task
      this.isLoadingFreshTask = true
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
      this.$store.dispatch('reloadTasks', this.activeSchoolYear.id).then(() => {
        let fresh = this.tasks.find(t => t.id === task.id)
        if (!fresh && task.subject_ids && task.subject_ids.length) {
          const cachedSubjectIds = new Set(task.subject_ids)
          fresh = this.tasks.find(t => (t.subject_ids || []).some(sid => cachedSubjectIds.has(sid)))
        }
        if (fresh) {
          this.editTask = fresh
        }
        this.isLoadingFreshTask = false
      })
    },
    openCreateTask () {
      this.editTask = null
      this.prefillTask = null
      this.isQuickCreate = false
      this.isTaskModal = true
    },
    closeCreateTask () {
      this.isTaskModal = false
      this.editTask = null
      this.prefillTask = null
      this.isQuickCreate = false
    },
    duplicateFromGroup () {
      if (!this.activeGroupData) return
      const ref = this.activeGroupData.tasks[0]
      this.editTask = null
      this.isQuickCreate = true
      this.prefillTask = {
        name: this.activeGroupData.name,
        color: this.activeGroupData.color,
        subject_type: ref ? ref.subject_type : 'additional_tasks',
        can_schedule: ref ? ref.can_schedule : true,
        default_duration: ref ? ref.default_duration : 60,
        preparation: ref ? ref.preparation : null,
        in_user_calendar: ref ? ref.in_user_calendar : true,
        belongs_to_calendars: ref ? ref.belongs_to_calendars : []
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    duplicateTask (task) {
      this.editTask = null
      this.isQuickCreate = true
      this.prefillTask = {
        name: task.name,
        color: task.color,
        subject_type: task.subject_type,
        can_schedule: task.can_schedule,
        default_duration: task.default_duration,
        preparation: task.preparation,
        in_user_calendar: task.in_user_calendar,
        belongs_to_calendars: task.belongs_to_calendars || []
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    createTask (task, {keepOpen = false} = {}) {
      task['schoolYearId'] = this.activeSchoolYear.id
      this.$store.dispatch('createTask', task)
      if (!keepOpen) {
        this.closeCreateTask()
        return
      }
      this.isTaskModal = false
      setTimeout(() => {
        this.isTaskModal = true
      }, 0)
    },
    updateTask (updatesForTask) {
      this.$store.dispatch('updateTask', updatesForTask)
      this.closeCreateTask()
    },
    setActiveDSelect (task) {
      if ((task.user_calendar_ids || []).length < 1) {
        this.activeDSelectShow = true
      } else {
        this.activeDSelectShow = false
      }
      this.activeDSelect = task.id
    },
    closeDSelect () {
      if (!this.activeDSelect) return
      this.activeDSelect = null
    },
    setTableSelectedUsers (users, task) {
      const nonUserCalIds = task._nonUserCalendarIds || []
      const userCalendars = users.map(u => u.id).concat(nonUserCalIds)
      let belongsToCalendars = []
      if (!task.in_user_calendar) {
        belongsToCalendars = task.belongs_to_calendars[0]
      } else {
        belongsToCalendars = userCalendars
      }
      const updatedTask = {
        schoolId: task.school_id,
        schoolYearId: task.school_year_id,
        id: task.id,
        name: task.name,
        color: task.color,
        hours: task.hours,
        defaultDuration: task.default_duration,
        preparation: task.preparation != null ? Number(task.preparation) : null,
        belongsToCalendars: belongsToCalendars,
        userCalendars: userCalendars,
        inUserCalendar: task.in_user_calendar
      }
      this.$store.dispatch('updateTask', updatedTask)
    },
    dataToExcel () {
      if (!this.activeGroupData) return []
      return this.activeGroupData.tasks.map(task => {
        return {
          name: task.name || '',
          hours: parseFloat(task.hours) || 0,
          type: this.getTaskTypeLabel(task),
          users: this.getUserNames(task.user_calendar_ids || []).join(', '),
          total: this.getTaskWeightedTotal(task)
        }
      })
    },
    asyncLoadXLSXAndDownload () {
      this.isDownloading = true
      if (window.XLSX && XLSX) {
        this.downloadExcel()
      } else {
        const cdnXLSX = document.createElement('script')
        cdnXLSX.setAttribute('src', 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.mini.min.js')
        document.head.appendChild(cdnXLSX)
        const intervalTimer = setInterval(() => {
          if (window.XLSX) {
            clearInterval(intervalTimer)
            XLSX = window.XLSX
            this.downloadExcel()
          }
        }, 500)
      }
    },
    downloadExcel () {
      const t = this.i18n[this.locale]
      const headers = {
        name: t['task'],
        hours: t['hours'],
        type: t['type'],
        users: t['users'],
        total: t['total']
      }
      const rows = this.dataToExcel()
      rows.unshift(headers)

      const sheet = XLSX.utils.json_to_sheet(rows, {skipHeader: true})
      const wb = XLSX.utils.book_new()
      const groupName = this.activeGroupName === '__all__' ? t['all'] : this.activeGroupName
      XLSX.utils.book_append_sheet(wb, sheet, t['tasks_sheet'])
      XLSX.writeFile(wb, t['tasks_sheet'].toLowerCase() + '_' + groupName.replace(/[^a-zA-Z0-9æøåÆØÅ]/g, '_') + '.xlsx')

      this.isDownloading = false
    }
  }
}
</script>

<style scoped>
@import '../../assets/css/variables.css';

.tasks-layout {
  display: flex;
  flex-direction: column;
  height: 100%;
  min-height: 400px;
}

.tasks-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  position: sticky;
  top: 0;
  z-index: 6;
  border-bottom: 1px solid var(--border-color);
  padding-top: 0.5rem;
  padding-bottom: 0.5rem;
}

.tasks-header__right,
.tasks-header__left {
  display: flex;
  align-items: center;
  gap: 1rem;
}

.tasks-body {
  display: flex;
  flex: 1;
  min-height: 0;
}

.tasks-sidebar {
  width: 260px;
  min-width: 260px;
  border-right: 1px solid #e2e8f0;
  background: #f8fafc;
  overflow-y: auto;
  transition: width 0.2s, min-width 0.2s;
}

.tasks-sidebar--collapsed {
  width: 42px;
  min-width: 42px;
  overflow: hidden;
}

.sidebar-header {
  display: flex;
  align-items: center;
  justify-content: space-between;
  padding: 12px 16px;
  font-size: 11px;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #8898aa;
  border-bottom: 1px solid #e2e8f0;
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
}

.sidebar-create-btn {
  padding: 2px 10px;
  font-size: 14px;
  line-height: 1;
  min-height: 0;
}

.sidebar-collapse-btn {
  background: none;
  border: none;
  color: #8898aa;
  cursor: pointer;
  padding: 2px 4px;
  font-size: 12px;
  line-height: 1;
  flex-shrink: 0;
  transition: color 0.15s;
}

.sidebar-collapse-btn:hover {
  color: #525f7f;
}

.tasks-sidebar--collapsed .sidebar-header {
  justify-content: center;
  padding: 12px 8px;
}

.sidebar-list {
  list-style: none;
  margin: 0;
  padding: 0;
}

.sidebar-item {
  display: flex;
  align-items: center;
  padding: 10px 16px;
  cursor: pointer;
  border-bottom: 1px solid rgba(226, 232, 240, 0.5);
  font-size: 13px;
  transition: background 0.1s;
}

.sidebar-item:hover {
  background: #edf2f7;
}

.sidebar-item--active {
  background: #ffffff;
  box-shadow: inset 3px 0 0 var(--primary);
  font-weight: 600;
}

.sidebar-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 10px;
  flex-shrink: 0;
}

.sidebar-name {
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.sidebar-count {
  font-size: 11px;
  color: #8898aa;
  margin-left: 8px;
  flex-shrink: 0;
}

.tasks-main {
  flex: 1;
  display: flex;
  flex-direction: column;
  overflow: hidden;
  background: #ffffff;
}

.thead-summary th {
  background: #f8fafc;
  padding: 10px 12px;
  border-bottom: none;
  box-shadow: inset 0 -1px 0 #e2e8f0;
}

.thead-summary__dot {
  display: inline-block;
  width: 10px;
  height: 10px;
  border-radius: 3px;
  margin-right: 8px;
  vertical-align: middle;
}

.thead-summary__name {
  font-size: 14px;
  font-weight: 600;
  margin-right: 10px;
  text-transform: none;
  letter-spacing: 0;
}

.thead-summary__meta {
  font-size: 12px;
  color: #8898aa;
  font-weight: 400;
  text-transform: none;
  letter-spacing: 0;
}

.task-table-wrap {
  flex: 1;
  min-height: 0;
  overflow-y: auto;
}

.task-table-wrap .d-table {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}

.task-table-wrap .d-table thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #f8fafc;
  text-align: left;
  padding: 8px 12px;
  font-weight: 600;
  font-size: 11px;
  text-transform: uppercase;
  letter-spacing: 0.03em;
  color: #8898aa;
  height: 52.5px;
  border-bottom: none;
  box-shadow: inset 0 -1px 0 #e2e8f0;
}

.task-table-wrap .d-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f0f3f7;
}

.task-table-row {
  cursor: pointer;
  transition: background 0.1s;
}

.task-table-row:hover {
  background: #f8fafc;
}

.task-table-row--add {
  color: #8898aa;
  font-style: italic;
}

.task-table-row--add:hover {
  color: var(--primary);
}

.task-table-dot {
  display: inline-block;
  width: 8px;
  height: 8px;
  border-radius: 2px;
  margin-right: 6px;
}

.task-table-type {
  font-size: 12px;
  color: #8898aa;
}

.task-table-actions {
  text-align: right;
  width: 40px;
}

.task-table-copy {
  border: none;
  background: rgba(0, 0, 0, 0.06);
  color: #525f7f;
  width: 22px;
  height: 22px;
  border-radius: 3px;
  font-size: 14px;
  font-weight: 700;
  cursor: pointer;
  opacity: 0;
  transition: opacity 0.1s;
  line-height: 1;
  padding: 0;
}

.task-table-row:hover .task-table-copy {
  opacity: 1;
}

.task-table-copy:hover {
  background: rgba(0, 0, 0, 0.12);
}

.task__name {
  display: flex;
  align-items: center;
  padding-left: 0.5rem;
}

.task__name .td-subject--hidden {
  background: transparent !important;
}

.td-subject {
  position: relative;
  padding: 4px 6px;
  height: 30px;
  color: #ffffff !important;
  border-radius: 4px;
  word-break: break-all;
  background: #2c81ba;
  overflow: hidden;
  box-shadow: 1px 1px 5px rgb(0 0 0 / 30%);
  text-align: left;
  min-width: auto;
  font-weight: bold;
  transition: transform 0.25s;
  cursor: pointer;
  width: 220px;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.td-subject--hidden {
  width: 220px;
  border: 1px solid transparent;
  padding: 4px 6px;
  border-radius: 4px;
  height: 30px;
  cursor: pointer;
  text-overflow: ellipsis;
  white-space: nowrap;
  overflow: hidden;
  position: relative;
}

.td-subject--hidden:hover {
  border-color: var(--border-color);
}

.td-subject--hidden:hover:before {
  content: "\e90e";
  font-family: 'icomoon' !important;
  speak: none;
  font-style: normal;
  font-weight: normal;
  font-variant: normal;
  text-transform: none;
  line-height: 1;
  -webkit-font-smoothing: antialiased;
  position: absolute;
  top: 8px;
  right: 4px;
  opacity: 0.7;
}

.d-select-lookalike {
  height: 42px;
  position: relative;
}

.d-select-lookalike:after {
  content: 'Søg og vælg';
  border-radius: 4px;
  display: flex;
  flex-wrap: wrap;
  transition: width 0.25s;
  padding-top: 5px;
  padding-bottom: 5px;
  padding-left: 9px;
  background: #ffffff;
  cursor: text;
  border: 1px solid #acb6be;
}

.task__users {
  min-height: 22px;
  width: 100%;
  border-radius: 4px;
  padding: 4px;
  cursor: pointer;
  border: 1px solid transparent;
  transition: border 0.1s;
}

.task__users:hover {
  border: 1px solid #acb6be;
}

.task__users > p {
  display: inline-block;
  margin: 0;
}

.task__users > p:after {
  content: ',';
  margin-right: 5px;
}

.task__users > p:last-child:after {
  content: '';
}

.main-empty {
  display: flex;
  align-items: center;
  justify-content: center;
  height: 200px;
  color: #8898aa;
  font-style: italic;
  font-size: 14px;
}

.sidebar-empty {
  padding: 16px;
  font-style: italic;
  color: #8898aa;
  font-size: 13px;
}
</style>



// WEBPACK FOOTER //
// SubjectsDistributionTasks.vue?4f356cb6